import { createAction } from 'redux-act';
import { push } from 'connected-react-router';
import { notification } from 'antd';
import get from 'lodash/get';
import qs from 'qs';
import feathersClient from 'services';

import { setLoading } from '../loading/actions';
import { fetchOrganization } from '../manageOrganizations/actions';
import { USER_TYPES_MAP } from 'utils/constants';
import {
  AUTHENTICATE,
  INVALIDATE_TOKENS,
  LOGIN,
  MFA_EMAIL_TOKEN,
  REDIRECT_TOKEN,
} from '../../graphql/modules/auth';
import { apolloClient } from '../../graphql/apollo-client';
import { saveToken, getAccessToken, getRefreshToken, getRedirectUrl } from '../../utils/auth';
import { isAllowableUserType } from '../../utils/user-type';
import { REDIRECT_PATH_PARAM, SERVER_URL } from '../../config/constants';
import * as Sentry from '@sentry/react';
import { resetOnUnauthenticate } from '../../utils/reset-on-unauthenticate';
import {
  emailLoginAttemptAnalyticsTrack,
  emailLoginFailedAnalyticsTrack,
  emailLoginSuccessAnalyticsTrack,
} from '../../lib/analytics/login/utils';
import { setSentryTagForUser } from '../../utils/sentry-tag';

export const handleAuthSuccess = createAction('handleAuthSuccess');
export const handleAuthFailure = createAction('handleAuthFailure');
export const handleAuthWithMFA = createAction('handleAuthWithMFA');
export const signout = createAction('signout');

const pathIsSuperAdminRoute = (path) => /^\/admin(\/|$)/.test(path);
const pathIsOauthRoute = (path) => /^\/handle-oauth-login(\/|$)/.test(path);

const TOKEN_MUTATION_MAP = {
  mfaEmailToken: MFA_EMAIL_TOKEN,
  redirectToken: REDIRECT_TOKEN,
};

export const authCurrentUser = () => {
  return async (dispatch) => {
    try {
      const { redirectToken, mfaEmailToken } = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      });

      if (redirectToken) {
        const redirected = await authForRedirectToken(redirectToken, { dispatch });

        if (redirected) {
          return;
        }
      }

      if (mfaEmailToken) {
        await authForMfaEmailToken(mfaEmailToken, { dispatch });
      }

      await authForAccessToken(getAccessToken(), { dispatch });
    } finally {
      dispatch(setLoading({ key: 'app', value: false }));
    }
  };
};

export function login({ email, password, recaptchaVal }) {
  return async (dispatch) => {
    const key = 'login';
    try {
      dispatch(setLoading({ key, value: true }));

      emailLoginAttemptAnalyticsTrack();

      const { data } = await apolloClient.mutate({
        mutation: LOGIN,
        variables: { email, password, recaptchaVal },
      });

      if (!data) throw new Error('Something went wrong!');

      const { user, isMFAEnabled, mfaUser } = data.login;

      if (isMFAEnabled) {
        dispatch(handleAuthWithMFA({ mfaUser, isMFAEnabled }));
        return;
      }

      await feathersClient
        .authenticate({
          strategy: 'jwt',
          accessToken: data.login.accessToken,
        })
        .catch((e) => {
          Sentry.captureMessage('Error attempting to authenticate to FeathersJS Rest Client', {
            extra: {
              flow: 'login',
              apiType: 'rest',
              url: SERVER_URL,
              errorMessage: e.message,
              errorCode: e.code,
            },
          });

          throw e;
        });

      if (!isAllowableUserType(user)) {
        notification.error({
          message: 'You don\'t have permission',
        });

        dispatch(handleSignout());
        return;
      }

      setSentryTagForUser(user);
      saveToken(data.login);

      const redirectQueryString = new URLSearchParams(window.location.search).get(
        REDIRECT_PATH_PARAM
      );

      emailLoginSuccessAnalyticsTrack();

      const unauthorizedRedirect =
        user.userType !== USER_TYPES_MAP.meeoppStaff && pathIsSuperAdminRoute(redirectQueryString);

      if (unauthorizedRedirect) dispatch(push('/organization/home'));

      dispatch(handleAuthSuccess({ user, accessToken: data.login.accessToken }));

      dispatch(fetchOrganization({ id: user.organizationId }));
    } catch (err) {
      dispatch(handleAuthFailure(err));
      notification.error({
        message: err.message,
      });

      emailLoginFailedAnalyticsTrack(err);
    } finally {
      dispatch(setLoading({ key, value: false }));
    }
  };
}

export function handleSignout() {
  return async (dispatch) => {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    if (refreshToken) {
      await apolloClient.mutate({
        mutation: INVALIDATE_TOKENS,
        variables: { refreshToken, accessToken },
      });
    }

    dispatch(setLoading({ key: 'app', value: false }));
    if (accessToken) {
      await feathersClient.logout();
    }

    resetOnUnauthenticate();
    dispatch(signout());
  };
}

export function resetPasword(pathname) {
  return async (dispatch) => {
    const key = 'app';
    try {
      dispatch(push(pathname));
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setLoading({ key, value: false }));
    }
  };
}

const authWithTokenInURL = async (token, tokenType, { dispatch }) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: TOKEN_MUTATION_MAP[tokenType],
      variables: {
        [tokenType]: token,
      },
    });

    emailLoginSuccessAnalyticsTrack();

    return data[tokenType];
  } catch (e) {
    dispatch(handleSignout());
    emailLoginFailedAnalyticsTrack(e);
  }
};

const authForRedirectToken = async (redirectToken, { dispatch }) => {
  const { accessToken, refreshToken, isMFAEnabled, mfaUser } = await authWithTokenInURL(
    redirectToken,
    'redirectToken',
    { dispatch }
  );

  if (isMFAEnabled) {
    dispatch(handleAuthWithMFA({ mfaUser, isMFAEnabled }));
    dispatch(push('/login'));
    return true;
  }

  saveToken({ accessToken, refreshToken });
  return false;
};

const authForMfaEmailToken = async (mfaEmailToken, { dispatch }) => {
  const { accessToken, refreshToken } = await authWithTokenInURL(mfaEmailToken, 'mfaEmailToken', {
    dispatch,
  });

  saveToken({ accessToken, refreshToken });
  return false;
};

const authForAccessToken = async (accessToken, { dispatch }) => {
  if (!accessToken) {
    dispatch(handleSignout());
    return;
  }

  try {
    const { data } = await apolloClient.query({
      query: AUTHENTICATE,
    });

    if (!data) throw new Error();

    const { user, accessToken } = data.authenticate;
    await feathersClient
      .authenticate({
        strategy: 'jwt',
        accessToken,
      })
      .catch((e) => {
        Sentry.captureMessage('Error attempting to authenticate to FeathersJS Rest Client', {
          extra: {
            flow: 'authForAccessToken',
            apiType: 'rest',
            url: SERVER_URL,
            errorMessage: e.message,
            errorCode: e.code,
          },
        });

        throw e;
      });

    if (!isAllowableUserType(user)) {
      notification.error({
        message: 'You don\'t have permission',
      });

      dispatch(handleSignout());
      return;
    }

    setSentryTagForUser(user);
    saveToken({ accessToken });
    const isSuperAdminRoute = pathIsSuperAdminRoute(window.location.pathname);
    const isOauthRoute = pathIsOauthRoute(window.location.pathname);
    const isSchoolStaff = user.userType === USER_TYPES_MAP.schoolStaff;

    if ((isSuperAdminRoute && isSchoolStaff) || isOauthRoute) {
      const redirectInput = {
        user,
        window,
      };

      dispatch(push(getRedirectUrl({ redirectInput })));
    }

    dispatch(handleAuthSuccess({ user, accessToken }));

    dispatch(fetchOrganization({ id: user.organizationId }));
    dispatch(push(getRedirectUrl({ redirectInput: { user, window }, useCurrentPath: true })));
  } catch (err) {
    const code = get(err, 'code');
    const errorName = get(err, 'data.name');
    if (code === 401 && errorName === 'TokenExpiredError') {
      dispatch(handleSignout());
    } else {
      Sentry.captureException(err);
    }
  }
};
